<template>
    <div class="form-type-0">
        <form @submit.prevent="updateAccountDetails" @input="onInputChange" ref="form" novalidate>
            <CustomInput
                v-model="options.name"
                inputKey="invoice_name"
                type="text"
                ref="name"
                autocomplete="organization"
                required
            />

            <CustomInput
                v-model="options.address"
                inputKey="invoice_address"
                type="text"
                ref="address"
                autocomplete="street-address"
                required
            />

            <CustomInput
                v-model="options.postal_code"
                inputKey="invoice_postal_code"
                type="text"
                ref="postal_code"
                autocomplete="postal-code"
                required
            />

            <CustomInput
                v-model="options.city"
                inputKey="invoice_city"
                type="text"
                ref="city"
                autocomplete="address-level2"
                required
            />

            <CustomSelect
                v-model="options.country"
                inputKey="invoice_country"
                :options="countryOptions"
                ref="country"
                required
            />

            <CustomInput
                v-if="!(hideVat && options.country === 'nl')"
                v-model="options.vat"
                inputKey="invoice_vat"
                type="text"
                ref="vat"
                pattern="^[A-Z]{2}[A-Z0-9]{7,15}$"
                :required="options.country != 'nl' && options.country != 'gb'"
            />

            <CustomInput
                v-model="options.po_number"
                inputKey="invoice_po_number"
                type="text"
                ref="po_number"
            />

            <CustomInput
                v-model="options.invoice_email"
                inputKey="invoice_email"
                type="email"
                ref="invoice_email"
                autocomplete="email"
            />

            <CustomToggle
                v-model="options.consent"
                inputKey="options_legal"
                ref="options-legal"
                :options="consentOptions"
                v-if="!hideConsent"
            />

            <button v-if="!hideSaveButton || hasChanged" type="submit" :disabled="loading || !hasChanged">
                <span v-if="loading" class="loading-spinner-type-1"></span>
                <span v-else>{{ $t('forms.invoice_details.button') }}</span>
            </button>
            <p v-if="formError" class="form-error">{{ $t('forms.invoice_details.error') }}</p>
            <p v-if="formSuccess" class="form-success">{{ $t('forms.invoice_details.success') }}</p>
        </form>
    </div>
</template>

<script>
    import { useAccountStore } from '@/stores/account';
    import CustomInput from '@/components/inputs/CustomInput';
    import CustomSelect from '@/components/inputs/CustomSelect';
    import CustomToggle from '@/components/inputs/CustomToggle';
    import formMixin from '@/mixins/formMixin';

    export default {
        mixins: [formMixin],
        emits: ['saved', 'hasChanged'],
        props: {
            hideSaveButton: {
                type: Boolean,
                default: false
            },
            hideConsent: {
                type: Boolean,
                default: false
            },
            hideVat: {
                type: Boolean,
                default: false
            },
            tcOnly: {
                type: Boolean,
                default: false
            },
            initValidation: {
                type: Boolean,
                default: false
            }
        },
        components: {
            CustomInput,
            CustomSelect,
            CustomToggle
        },
        data() {
            return {
                optionsDefault: {
                    name: '',
                    address: '',
                    postal_code: '',
                    city: '',
                    vat: '',
                    po_number: '',
                    country: 'nl',
                    consent: this.tcOnly ? ['tc'] : ['tc', 'disclaimer'],
                    invoice_email: ''
                },
                options: {},
                countryOptions: [
                    { value: 'nl', text: 'fields.invoice_country.option_nl' },
                    { value: 'be', text: 'fields.invoice_country.option_be' },
                    { value: 'dk', text: 'fields.invoice_country.option_dk' },
                    { value: 'de', text: 'fields.invoice_country.option_de' },
                    { value: 'fi', text: 'fields.invoice_country.option_fi' },
                    { value: 'fr', text: 'fields.invoice_country.option_fr' },
                    { value: 'ie', text: 'fields.invoice_country.option_ie' },
                    { value: 'it', text: 'fields.invoice_country.option_it' },
                    { value: 'lu', text: 'fields.invoice_country.option_lu' },
                    { value: 'at', text: 'fields.invoice_country.option_at' },
                    { value: 'pt', text: 'fields.invoice_country.option_pt' },
                    { value: 'es', text: 'fields.invoice_country.option_es' },
                    { value: 'gb', text: 'fields.invoice_country.option_gb' },
                    { value: 'se', text: 'fields.invoice_country.option_se' },
                ],
                consentOptions: this.tcOnly ?
                [
                    { value: 'tc', text: 'fields.options_invoice_legal.options.tc', required: !this.hideConsent }
                ] :
                [
                    { value: 'tc', text: 'fields.options_invoice_legal.options.tc', required: !this.hideConsent },
                    { value: 'disclaimer', text: 'fields.options_invoice_legal.options.disclaimer', required: !this.hideConsent },
                ],
            };
        },
        computed: {
            accountStore() {
                return useAccountStore();
            }
        },
        methods: {
            async updateAccountDetails() {
                const form = this.$refs.form;
                if(form.checkValidity()) {
                    this.loading = true;

                    const data = {
                        invoice_name: this.options.name,
                        invoice_address: this.options.address,
                        invoice_postcode: this.options.postal_code,
                        invoice_city: this.options.city,
                        invoice_vat: this.options.vat,
                        invoice_po: this.options.po_number,
                        invoice_country_code: this.options.country,
                        invoice_email: this.options.invoice_email,
                    };

                    const success = await this.accountStore.updateAccount(data);
                    this.loading = false;

                    if (success) {
                        this.formSuccess = true;
                        this.formError = false;
                        this.hasChanged = false;
                        this.$emit('saved');
                    } else {
                        this.formSuccess = false;
                        this.formError = true;
                    }
                } else {
                    this.validateAllFields();
                }
            },
            setDefault() {
                this.options = {
                    ...this.optionsDefault,
                    ...((this.accountStore.account.invoice_name || this.accountStore.account.name) && { name: this.accountStore.account.invoice_name || this.accountStore.account.name }),
                    ...(this.accountStore.account.invoice_address && { address: this.accountStore.account.invoice_address }),
                    ...(this.accountStore.account.invoice_postcode && { postal_code: this.accountStore.account.invoice_postcode }),
                    ...(this.accountStore.account.invoice_city && { city: this.accountStore.account.invoice_city }),
                    ...(this.accountStore.account.invoice_po && { po_number: this.accountStore.account.invoice_po }),
                    ...(this.accountStore.account.invoice_vat && { vat: this.accountStore.account.invoice_vat }),
                    ...(this.accountStore.account.invoice_country_code && { country: this.accountStore.account.invoice_country_code }),
                    ...(this.accountStore.account.invoice_email && { invoice_email: this.accountStore.account.invoice_email }),
                };
            },
        },
        created() {
            this.setDefault();
        },
        mounted() {
            if(this.initValidation) {
                this.validateAllFields();
            }
        },
        watch: {
            hasChanged(val) {
                if(val) {
                    this.$emit('hasChanged', val);
                }
            }
        }
    }
</script>
